import templatesApi from '@/utils/api/templates';
import templatesApiV2 from '@/utils/api-v2/templates';
import Template from '@/models/templates/template';
import TemplateQuestion from '@/models/templates/templateQuestion';
import { getField, updateField } from 'vuex-map-fields';
import { downloadBase64Document } from '@/helpers/files';
import { TRANSLATION_KEYS } from '@/constants/questionTypes';
import { i18n } from '@/plugins/i18n';

export default {
    namespaced: true,
    state: () => ({
        activeTemplate: null,
        templateData: new Template(),
        templateFormVisibility: false,
        detailsVisibility: false,
        editedTemplateId: null,
        cloneTemplate: false
    }),
    getters: {
        getField,
        getAddedQuestions: state => {
            return state.templateData.questions;
        },
        getIsOsmTemplate: state => {
            return state.activeTemplate ? state.activeTemplate.is_osm_template : false;
        }

    },
    mutations: {
        updateField,
        SET_ACTIVE_TEMPLATE (state, payload) {
            state.activeTemplate = payload;
        },
        SET_TEMPLATE_FORM_VISIBILITY (state, payload) {
            state.templateFormVisibility = payload;
        },
        SET_CLONE_TEMPLATE (state, payload) {
            state.cloneTemplate = payload;
        },
        SET_DETAILS_VISIBILITY (state, payload) {
            state.detailsVisibility = payload;
        },
        SET_EDITED_TEMPLATE_ID (state, payload) {
            state.editedTemplateId = payload;
        },
        RESET_TEMPLATE_DATA (state) {
            state.templateData = new Template();
        },
        SET_TEMPLATE_DATA (state, payload) {
            state.templateData.parseData(payload);
        },
        CLONE_TEMPLATE_DATA (state, payload) {
            state.templateData.parseData(payload);
        },
        SET_QUESTION (state, payload) {
            if (payload.includeInScanField) {
                for (const q of state.templateData.questions) {
                    q.includeInScanField = false;
                }
            }
            if (payload.autocomplete) {
                for (const q of state.templateData.questions) {
                    q.autocomplete = false;
                }
            }
            if (payload.random) {
                payload.sequence = -1;
            }
            state.templateData.questions.push(payload);
        },
        EDIT_QUESTION (state, payload) {
            if (payload.includeInScanField) {
                for (const q of state.templateData.questions) {
                    q.includeInScanField = false;
                }
            }
            if (payload.autocomplete) {
                for (const q of state.templateData.questions) {
                    q.autocomplete = false;
                }
            }
            if (payload.random) {
                payload.sequence = -1;
            }
            const index = state.templateData.questions.findIndex(el => el.id === payload.id);

            state.templateData.questions.splice(index, 1, payload);
        },
        DELETE_QUESTION (state, id) {
            const index = state.templateData.questions.findIndex(el => el.id === id);

            state.templateData.questions.splice(index, 1);
        },
        SET_QUESTIONS_LIST (state, payload) {
            state.templateData.questions = payload;
        },
        REORDER_QUESTIONS (state) {
            state.templateData.reorderQuestions();
        }
    },
    actions: {
        getActiveTemplate ({ commit }, id) {
            return templatesApiV2.getActiveTemplate(id)
                .then(({ data }) => {
                    const questions = [];

                    templatesApiV2.getTemplateQuestions(id).then(({ data: questionsData }) => {
                        questionsData.forEach(el => {
                            const result = {
                                id: el.id,
                                type: el.question_type,
                                typeName: i18n.t(TRANSLATION_KEYS[el.question_type]),
                                question: el.question,
                                sequence: el.seq,
                                parameters: el.question_parameters ? JSON.parse(el.question_parameters) : [],
                                condition: el.minimum,
                                smsContent: el.sms_message_content,
                                repeated: el.repeatable,
                                required: el.required,
                                notApplicable: el.not_applicable,
                                specialAttention: el.special_attention,
                                includeInScanField: el.include_in_scan_field,
                                autocomplete: el.autocomplete,
                                display_type: el.display_type,
                                osmQuestionType: el.osm_question_type
                            };

                            if (data.is_osm_template) {
                                result.osmQuestionType = el.osm_question_type;
                            }

                            if (el.incident_type) {
                                result.incident = el.incident_type.id;
                                result.incidentName = el.incident_type.name;
                            }

                            const question = new TemplateQuestion(result);

                            questions.push(question);
                        });
                    });

                    commit('SET_ACTIVE_TEMPLATE', {
                        ...data,
                        questions: questions
                    });

                    commit('SET_EDITED_TEMPLATE_ID', data.id);
                });
        },
        addQuestion ({ rootState, commit }) {
            commit('SET_QUESTION', rootState.manageTemplateQuestion.questionData);
        },
        editQuestion ({ rootState, commit }) {
            commit('EDIT_QUESTION', rootState.manageTemplateQuestion.questionData);
        },
        deleteQuestion ({ commit }, id) {
            const promise = new Promise((resolve, reject) => {
                commit('DELETE_QUESTION', id);
                resolve();
            });
            return promise
                .then(() => {
                    commit('REORDER_QUESTIONS');
                });
        },
        createTemplate ({ state }, id) {
            const data = state.templateData.getDataForCreate(state.cloneTemplate ? null : id);
            return templatesApi.createTemplate(data);
        },
        updateTemplate ({ state }, id) {
            const data = state.templateData.getDataForUpdate(id);

            return templatesApi.updateTemplate(data);
        },
        deleteTemplate (context, id) {
            return templatesApi.deleteTemplate(id);
        },
        clearActiveTemplateData ({ commit }) {
            commit('SET_EDITED_TEMPLATE_ID', null);
            commit('SET_ACTIVE_TEMPLATE', null);
            commit('RESET_TEMPLATE_DATA');
        },
        exportTemplate ({ state }) {
            return templatesApi.exportTemplate(state.editedTemplateId)
                .then(({ data }) => {
                    downloadBase64Document(data, state.activeTemplate.name, 'application/json');
                });
        },
        importTemplate (context, data) {
            return templatesApi.importTemplate(data);
        },
        manageArchiveTemplate (context, data) {
            return templatesApi.manageArchiveTemplate(data);
        }
    }
};
